<!-- @format -->
<template>
    <div class="user-widget">
        <loading-svg v-if="loading" />
        <div v-else class="widget-contents-container">
            <div class="widget-header">
                <h3 v-if="user.id">Edit User</h3>
                <h3 v-else>Invite a User</h3>
                <div class="svg-container widget-close-button" @click="closeWidget()">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div>
                    <div v-if="!user.id" class="intro-container">
                        <p>
                            Ready to invite a user to GlassHive? Awesome! Use the form below to get
                            started.
                        </p>
                    </div>

                    <div v-if="showBillingNotice" class="billing-notice">
                        <t3-alert class="billing-notice-icon"></t3-alert>
                        <div class="billing-notice-content">
                            <div class="billing-notice-title">Billing Notice</div>
                            <div class="billing-notice-message">
                                <template v-if="isOnFreeTrial">
                                    Inviting an additional user to your GlassHive account is free
                                    during your trial. Your subscription will increase by
                                    {{ nextBillingAmountDifferenceFormatted }} when billing begins
                                    on {{ freeTrialEndDateFormatted }}.
                                </template>
                                <template v-else-if="!isBeingBilled">
                                    Inviting an additional user to your GlassHive account will incur
                                    a charge of {{ nextBillingAmountFormatted }} on
                                    {{ nextBillingDateFormatted }} which will initiate a new monthly
                                    billing cycle.
                                </template>
                                <template v-else>
                                    Inviting an additional user to your GlassHive account will
                                    increase your monthly subscription by
                                    {{ nextBillingAmountDifferenceFormatted }}, and this change will
                                    take effect in your next billing cycle.
                                </template>
                            </div>
                        </div>
                    </div>

                    <div class="section">
                        <div class="section-label">User Information</div>
                        <div class="section-container">
                            <div class="input-container">
                                <image-upload
                                    v-if="user.id"
                                    id="user-image-upload"
                                    :upload-url="uploadUrl"
                                    :placeholder-image="placeholderImage"
                                    :provided-image-url="user.profileImageUrl"
                                    orientation="horizontal"
                                    @fileUploadSuccess="updateUserImageUrl"
                                />
                            </div>
                            <div class="input-container">
                                <label>
                                    <span
                                        :class="{
                                            required:
                                                $v.user.firstName.$dirty &&
                                                $v.user.firstName.$error,
                                        }"
                                        >First Name</span
                                    >
                                    <span class="asterisk">*</span>
                                </label>
                                <app-input
                                    v-model="user.firstName"
                                    placeholder="First Name"
                                    :is-valid="!$v.user.firstName.$error"
                                    :error="
                                        $v.user.firstName.$dirty && !$v.user.firstName.required
                                            ? 'Required'
                                            : ''
                                    "
                                />
                            </div>
                            <div class="input-container">
                                <label>
                                    <span
                                        :class="{
                                            required:
                                                $v.user.lastName.$dirty && $v.user.lastName.$error,
                                        }"
                                        >Last Name</span
                                    >
                                    <span class="asterisk">*</span>
                                </label>
                                <app-input
                                    v-model="user.lastName"
                                    placeholder="Last Name"
                                    :is-valid="!$v.user.lastName.$error"
                                    :error="
                                        $v.user.lastName.$dirty && !$v.user.lastName.required
                                            ? 'Required'
                                            : ''
                                    "
                                />
                            </div>
                            <div class="input-container">
                                <label>
                                    <span>Title</span>
                                </label>
                                <app-input v-model="user.title" placeholder="Title" />
                            </div>
                            <div class="input-container">
                                <label>
                                    <span
                                        :class="{
                                            required: $v.user.email.$dirty && $v.user.email.$error,
                                        }"
                                        >Email</span
                                    >
                                    <span class="asterisk">*</span>
                                </label>
                                <app-input
                                    v-model="user.email"
                                    placeholder="email@email.com"
                                    :is-valid="!$v.user.email.$error"
                                    :error="
                                        $v.user.email.$dirty && !$v.user.email.required
                                            ? 'Required'
                                            : ''
                                    "
                                />
                            </div>
                        </div>
                    </div>

                    <div class="section">
                        <div class="section-label">Signature</div>
                        <div class="section-container">
                            <app-text-editor
                                ref="textEditor"
                                v-model="user.signature"
                                :config="config"
                                :show-branding-colors="false"
                            />
                        </div>
                    </div>

                    <div class="section">
                        <div class="section-label">User Role<span class="asterisk">*</span></div>
                        <div
                            class="section-container"
                            :class="{ 'has-role-errors': hasRoleErrors && $v.user.$error }"
                        >
                            <div v-if="isAgencyPage">
                                <div class="checkbox-section">
                                    <app-checkbox
                                        v-model="user.hasAgencyAdminPermission"
                                        :is-valid="getIsValidField('hasAgencyAdminPermission')"
                                        :disabled="isCurrentUser"
                                        :class="[{ disable: isCurrentUser }]"
                                    />
                                    <label>
                                        <strong>Admin</strong>
                                        <ul>
                                            <li>
                                                <span
                                                    >• An admin has the authority to perform any
                                                    action, including viewing, creating, editing,
                                                    and deleting records on this vendor account.
                                                </span>
                                            </li>
                                        </ul>
                                    </label>
                                </div>
                                <div v-if="isCurrentUser" class="current-user-error">
                                    You can't change your own Admin role. Ask another Admin to edit
                                    it.
                                </div>
                                <div class="checkbox-section">
                                    <app-checkbox
                                        v-model="user.hasMarketingCoordinatorPermission"
                                        :is-valid="
                                            getIsValidField('hasMarketingCoordinatorPermission')
                                        "
                                    />
                                    <label>
                                        <strong>Marketing Coordinator</strong>
                                        <ul>
                                            <li>
                                                <span
                                                    >• Perform any marketing function for any
                                                    partner connected to the vendor account.</span
                                                >
                                            </li>
                                            <li>
                                                <span
                                                    >• Be able to view marketing and partner
                                                    performance analytics.</span
                                                >
                                            </li>
                                        </ul>
                                    </label>
                                </div>
                                <div class="checkbox-section">
                                    <app-checkbox
                                        v-model="user.hasAccountsManager"
                                        :is-valid="getIsValidField('hasAccountsManager')"
                                    />
                                    <label>
                                        <strong>Account Manager</strong>
                                        <ul>
                                            <li>
                                                <span
                                                    >• May only view and interact with partner
                                                    accounts that are assigned to them.</span
                                                >
                                            </li>
                                            <li>
                                                <span
                                                    >• Can approve, deny, edit, and allocate funds
                                                    for Marketing Development Funds (MDF).</span
                                                >
                                            </li>
                                        </ul>
                                    </label>
                                </div>
                            </div>
                            <div v-if="clientId">
                                <div class="checkbox-section">
                                    <app-checkbox
                                        v-model="user.hasAdminRole"
                                        :is-valid="!hasRoleErrors || !$v.user.$error"
                                    />
                                    <label>
                                        <strong>Admin</strong>
                                        <span
                                            >An Admin can do anything in GlassHive. This includes
                                            viewing, creating, editing, and deleting practically any
                                            record in this account. The only limit is that since an
                                            Admin is not a Salesperson, they cannot be assigned
                                            Contacts.</span
                                        >
                                    </label>
                                </div>
                                <div class="checkbox-section">
                                    <app-checkbox
                                        v-model="user.hasMarketerRole"
                                        :is-valid="!hasRoleErrors || !$v.user.$error"
                                    />
                                    <label>
                                        <strong>Marketer</strong>
                                        <span
                                            >Marketers are limited to the view of Marketing. They
                                            will not be able to view Sales analytics.
                                        </span>
                                    </label>
                                </div>
                                <div class="checkbox-section">
                                    <app-checkbox
                                        v-model="user.hasSalespersonRole"
                                        :is-valid="!hasRoleErrors || !$v.user.$error"
                                    />
                                    <label>
                                        <strong>Salesperson</strong>
                                        <span
                                            >Salespeople are limited to the view of Sales. They will
                                            not be able to view Marketing analytics.
                                        </span>
                                    </label>
                                </div>
                                <div class="checkbox-section">
                                    <app-checkbox
                                        v-model="user.hasMDFManagerRole"
                                        :is-valid="!hasRoleErrors || !$v.user.$error"
                                    />
                                    <label>
                                        <strong>MDF Manager</strong>
                                        <ul>
                                            <li><span>• May request MDF from Vendors.</span></li>
                                            <li><span>• May delete MDF requests.</span></li>
                                            <li>
                                                <span
                                                    >• Adds and manages Proof of Performance for MDF
                                                    requests.</span
                                                >
                                            </li>
                                        </ul>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div v-if="hasRoleErrors && $v.user.$error" class="error-role-message">
                            Please select at least one role.
                        </div>
                    </div>
                    <div class="section">
                        <div class="section-label">User Permissions</div>
                        <div class="section-container">
                            <div v-if="isAgencyPage">
                                <div class="checkbox-section">
                                    <app-checkbox v-model="user.hasBillingPermission" />
                                    <label>
                                        <strong>Billing and Payment</strong>
                                        <ul>
                                            <li>
                                                <span>• Add, delete and use payment methods.</span>
                                            </li>
                                            <li>
                                                <span>• Authorizes the payment of invoices.</span>
                                            </li>
                                            <li>
                                                <span
                                                    >• Will receive all invoice and billing related
                                                    emails.</span
                                                >
                                            </li>
                                        </ul>
                                    </label>
                                </div>
                                <div class="checkbox-section">
                                    <app-checkbox v-model="user.hasFundsManagerPermission" />
                                    <label>
                                        <strong>Funds Manager</strong>
                                        <ul>
                                            <li>
                                                <span>• Adds and manages <em>MDF Budget</em></span>
                                            </li>
                                            <li>
                                                <span
                                                    >• Sets up and manages
                                                    <em>Automated Funds Approval</em> for MDF</span
                                                >
                                            </li>
                                        </ul>
                                    </label>
                                </div>
                                <div class="checkbox-section">
                                    <app-checkbox
                                        v-model="user.hasMDFRequestSettingsManagerPermission"
                                    />
                                    <label>
                                        <strong>MDF Request Settings Manager</strong>
                                        <ul>
                                            <li>
                                                <span
                                                    >• Adds and manages
                                                    <em>Activity Types and Proof of Performance</em>
                                                    for partner MDF requests</span
                                                >
                                            </li>
                                            <li>
                                                <span
                                                    >• Adds and manages
                                                    <em>Guidelines and Requirements</em> for partner
                                                    MDF requests</span
                                                >
                                            </li>
                                        </ul>
                                    </label>
                                </div>
                                <div class="checkbox-section">
                                    <app-checkbox v-model="user.hasFundsPayerPermission" />
                                    <label>
                                        <strong>Funds Payer</strong>
                                        <ul>
                                            <li>
                                                <span
                                                    >• Pays partners once funds are approved by
                                                    <em>POP Submissions Manager</em></span
                                                >
                                            </li>
                                        </ul>
                                    </label>
                                </div>
                                <div class="checkbox-section">
                                    <app-checkbox
                                        v-model="user.hasPOPSubmissionsManagerPermission"
                                    />
                                    <label>
                                        <strong>POP Submissions Manager</strong>
                                        <ul>
                                            <li>
                                                <span
                                                    >• Approves or denies MDF
                                                    <em>POP Submissions</em></span
                                                >
                                            </li>
                                            <li>
                                                <span
                                                    >• May edit previously approved or denied
                                                    <em>MDF requests</em></span
                                                >
                                            </li>
                                        </ul>
                                    </label>
                                </div>
                            </div>
                            <div class="checkbox-section">
                                <app-checkbox v-model="user.hasReadOnlyRole" />
                                <label>
                                    <strong>Read-Only Access</strong>
                                    <ul>
                                        <li>
                                            <span
                                                >• By enabling Read-Only Access, you overwrite the
                                                users ability to make changes while still allowing
                                                them to have visibility.</span
                                            >
                                        </li>
                                    </ul>
                                </label>
                            </div>
                            <div class="checkbox-section">
                                <app-checkbox v-model="user.hasSendDocumentRole" />
                                <label>
                                    <strong>Send Documents</strong>
                                    <span
                                        >Send Documents By enabling Send Documents, the user will be
                                        able to approve or send out documents to contacts through
                                        the Document Builder.</span
                                    >
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="transparent-btn" @click="closeWidget()"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button @click="save()">
                        {{ user.id ? 'Save' : 'Send Invite' }}
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { sameAs, requiredIf, required } from 'vuelidate/lib/validators';
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const ImageUpload = () => import('@/components/common/image-upload.vue');
const AppCheckbox = () => import('@/components/common/app-checkbox');
const AppInput = () => import('@/components/common/app-input');
const AppButton = () => import('@/components/common/app-button');
const AppTextEditor = () => import('@/components/common/app-text-editor');
const LoadingSvg = () => import('@/components/svg/loading-screen-svg');

import EventBus from '@/scripts/event-bus.js';
import log, { LogLevel } from '@/scripts/logger';
import T3Alert from '@/components/svg/grid/t3-alert.vue';
import { PromoService } from '@/scripts/services/entities/promo-service';
import { ClientService } from '@/scripts/services/entities/client-service';
import { InvoicesService } from '@/scripts/services/invoices/invoices-service';

export default {
    name: 'user-widget',

    components: {
        't3-alert': T3Alert,
        'widget-close-svg': WidgetCloseSvg,
        'app-input': AppInput,
        'app-checkbox': AppCheckbox,
        'image-upload': ImageUpload,
        'app-button': AppButton,
        'app-text-editor': AppTextEditor,
        'loading-svg': LoadingSvg,
    },

    props: {
        options: {
            type: Object,
        },

        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            clientId: this.widget.options.clientId,
            agencyId: this.widget.options.agencyId,
            masterAgencyId: this.widget.options.masterAgencyId,
            user: {
                id: this.widget.options.userId,
                profileImageUrl: null,
                firstName: null,
                lastName: null,
                title: null,
                email: null,
                signature: null,
                clientId: null,
                hasSalespersonRole: false,
                hasMDFManagerRole: false,
                hasMarketerRole: false,
                hasAdminRole: false,
                hasMDFRequestSettingsManagerPermission: false,
                hasFundsManagerPermission: false,
                hasFundsPayerPermission: false,
                hasPOPSubmissionsManagerPermission: false,
                hasAccountsManager: false,
                hasBillingPermission: false,
                hasReadOnlyRole: false,
                hasSendDocumentRole: false,
                hasAgencyAdminPermission: false,
                hasMarketingCoordinatorPermission: false,
            },

            userLimit: null,
            userCount: null,
            uploadUrl: '/api/Files',
            placeholderImage: require('@/assets/images/avatar.png'),
            loading: false,

            config: {
                key: this.$helpers.getFroalaKey(),
                toolbarButtons: [
                    'paragraphFormat',
                    'fontFamily',
                    'fontSize',
                    'align',
                    'bold',
                    'italic',
                    'colorPicker',
                    '|',
                    'formatOL',
                    'formatUL',
                    'insertImage',
                    'insertLink',
                    'insertTable',
                    'undo',
                    'redo',
                ],
                fontFamilySelection: true,
                fontSizeSelection: true,
                paragraphFormatSelection: true,
                fontFamily: this.$helpers.minimalFontStyles,

                imageUploadParam: 'file',
                fileUploadParam: 'file',

                imageUploadURL: 'https://api.honeycrm.com/api/Files/Froala',
                fileUploadURL: 'https://api.honeycrm.com/api/Files/Froala',

                imageUploadMethod: 'POST',
                fileUploadMethod: 'POST',

                imageMaxSize: 4 * 1024 * 1024,
                fileMaxSize: 4 * 1024 * 1024,

                imageAllowedTypes: ['jpeg', 'jpg', 'png'],
                fileAllowedTypes: ['*'],

                requestHeaders: {
                    Authorization: 'Bearer ' + localStorage.jwt,
                },
            },

            hasRoleErrors: false,

            promo: {
                basePrice: 0,
                tier2UserPrice: 0,
                userPrice: 0,
                freeUsers: 0,
                maxTier1Users: 0,
            },

            freeTrialEndDate: null,
            nextBillingDate: null,
            invoices: [],
        };
    },

    computed: {
        routeBaseUrl() {
            return this.$store.getters.routeBaseUrl;
        },

        userId() {
            return this.$store.state.user.id;
        },

        clientManagesSubscription() {
            return this.$store.state.context.client.managesSubscription ?? false;
        },

        isUserLimitReached() {
            return this.userLimit !== null && this.userCount >= this.userLimit;
        },

        isAgencyPage() {
            return this.$store.state.context.agency.id;
        },

        isCurrentUser() {
            return this.userId === this.widget.options.userId;
        },

        isInvitingUser() {
            return this.clientId != null;
        },

        affectsBilling() {
            return this.widget.options.affectsBilling ?? false;
        },

        paymentMethods() {
            return this.widget.options.paymentMethods ?? [];
        },

        hasPaymentMethods() {
            return this.paymentMethods.length > 0;
        },

        hasInvoices() {
            return this.invoices.length > 0;
        },

        isBeingBilled() {
            return this.hasPaymentMethods && this.hasInvoices;
        },

        showBillingNotice() {
            return (
                this.isInvitingUser &&
                this.affectsBilling &&
                this.clientId &&
                this.clientManagesSubscription &&
                ((this.isOnFreeTrial && this.userCount > this.promo.freeUsers) ||
                    !this.isBeingBilled ||
                    this.nextBillingAmountDifference > 0)
            );
        },

        nextBillingAmount() {
            return PromoService.calculateBillingAmount(this.userCount + 1, {
                basePrice: this.promo.basePrice,
                freeUsers: this.promo.freeUsers,
                maxTier1Users: this.promo.maxTier1Users,
                tier2UserPrice: this.promo.tier2UserPrice,
                userPrice: this.promo.userPrice,
            });
        },

        nextBillingAmountDifference() {
            const currentBillingAmount = PromoService.calculateBillingAmount(this.userCount, {
                basePrice: this.promo.basePrice,
                freeUsers: this.promo.freeUsers,
                maxTier1Users: this.promo.maxTier1Users,
                tier2UserPrice: this.promo.tier2UserPrice,
                userPrice: this.promo.userPrice,
            });

            return Math.max(0, this.nextBillingAmount - currentBillingAmount);
        },

        nextBillingAmountFormatted() {
            return this.formatUSD(this.nextBillingAmount);
        },

        nextBillingAmountDifferenceFormatted() {
            return this.formatUSD(this.nextBillingAmountDifference);
        },

        isOnFreeTrial() {
            return this.freeTrialEndDate != null && new Date(this.freeTrialEndDate) > new Date();
        },

        freeTrialEndDateFormatted() {
            if (!this.freeTrialEndDate || !this.isOnFreeTrial) {
                return '';
            }

            const freeTrialEndDate = this.$moment(new Date(this.freeTrialEndDate));
            if (!freeTrialEndDate.isValid()) {
                return '';
            }

            return freeTrialEndDate.format('MM/DD/YYYY');
        },

        nextBillingDateFormatted() {
            const format = 'MM/DD/YYYY';

            if (!this.nextBillingDate) {
                const now = this.$moment.utc();
                let defaultBillingDate;

                if (now.hour() < 14) {
                    defaultBillingDate = now.local();
                } else {
                    defaultBillingDate = now.add(1, 'days').local();
                }

                return defaultBillingDate.format(format);
            }

            const nextBillingDate = this.$moment(new Date(this.nextBillingDate));
            if (!nextBillingDate.isValid()) {
                return '';
            }

            return nextBillingDate.format(format);
        },
    },

    validations() {
        const isRequired = !this.rolesValidation();
        return {
            user: {
                hasAgencyAdminPermission: {
                    required: requiredIf(() => {
                        return isRequired;
                    }),

                    ...(isRequired ? { sameAs: sameAs(() => true) } : false),
                },

                hasAccountsManager: {
                    required: requiredIf(() => {
                        return isRequired;
                    }),

                    ...(isRequired ? { sameAs: sameAs(() => true) } : false),
                },

                hasMarketingCoordinatorPermission: {
                    required: requiredIf(() => {
                        return isRequired;
                    }),

                    ...(isRequired ? { sameAs: sameAs(() => true) } : false),
                },

                firstName: {
                    required,
                },

                lastName: {
                    required,
                },

                email: {
                    required,
                },
            },
        };
    },

    async created() {
        if (this.user.id) {
            this.getUser();
        } else if (this.isInvitingUser) {
            await this.loadInviteUsersWidget();
        }
    },

    methods: {
        async loadInviteUsersWidget() {
            this.loading = true;

            try {
                await Promise.all([
                    this.loadClient(),
                    this.loadNextBillingDate(),
                    this.loadInvoices(),
                ]);
            } catch (error) {
                this.$helpers.showFailNotification('Failed to load data for inviting users.', {
                    error,
                });
            } finally {
                this.loading = false;
            }
        },

        async loadClient() {
            try {
                const res = await this.$api.get(
                    `/service/Clients?$filter=Id eq ${this.clientId}&$expand=Promo($select=UserLimit,BasePrice,UserPrice,FreeUsers,MaxTier1Users,Tier2UserPrice),Users($top=0;$count=true;$filter=IsActive eq true)`,
                );

                const data = res.data.value[0];

                this.userCount = data['Users@odata.count'];
                this.userLimit = data.Promo.UserLimit;

                const promo = data.Promo;

                this.promo = {
                    basePrice: promo.BasePrice ?? 0,
                    tier2UserPrice: promo.Tier2UserPrice ?? 0,
                    userPrice: promo.UserPrice ?? 0,
                    freeUsers: promo.FreeUsers ?? 0,
                    maxTier1Users: promo.MaxTier1Users ?? 0,
                };

                this.freeTrialEndDate = data.FreeTrialEndDate;
            } catch (error) {
                const msg = 'Failed to load promo and user count';
                log.error(msg);
                throw new Error(msg, { cause: error });
            }
        },

        async loadNextBillingDate() {
            try {
                const res = await ClientService.getNextBillingDate(this.clientId);
                const data = res.data;

                this.nextBillingDate = data;
            } catch (error) {
                const msg = 'Failed to load the next billing date';
                log.error(msg);
                throw new Error(msg, { cause: error });
            }
        },

        async loadInvoices() {
            try {
                const res = await InvoicesService.getAll(
                    `where=[{ "ClientId": ${this.clientId} }]&limit=1`,
                );
                const invoices = res.data.data;

                this.invoices = invoices ?? [];
            } catch (error) {
                const msg = 'Failed to load invoices';
                log.error(msg);
                throw new Error(msg, { cause: error });
            }
        },

        getUser() {
            this.loading = true;
            this.$api
                .get(`/service/Users?$filter=Id eq '${this.user.id}'`)
                .then((response) => {
                    var userResponse = response.data.value[0];

                    this.user.firstName = userResponse.FirstName;
                    this.user.lastName = userResponse.LastName;
                    this.user.title = userResponse.Title;
                    this.user.email = userResponse.Email;
                    this.user.signature = userResponse.Signature;
                    this.user.profileImageUrl = userResponse.ProfileImageUrl;

                    var roleIds = userResponse.Roles.map((role) => role.RoleId);

                    this.user.hasAdminRole = roleIds.includes('2');
                    this.user.hasSalespersonRole = roleIds.includes('6');

                    this.user.hasMDFManagerRole = roleIds.includes('19');
                    this.user.hasMarketerRole = roleIds.includes('7');
                    this.user.hasMDFRequestSettingsManagerPermission = roleIds.includes('12');
                    this.user.hasFundsManagerPermission = roleIds.includes('13');
                    this.user.hasFundsPayerPermission = roleIds.includes('14');
                    this.user.hasPOPSubmissionsManagerPermission = roleIds.includes('15');
                    this.user.hasAccountsManager = roleIds.includes('16');
                    this.user.hasAgencyAdminPermission = roleIds.includes('17');
                    this.user.hasMarketingCoordinatorPermission = roleIds.includes('18');
                    this.user.hasBillingPermission = roleIds.includes('20');
                    this.user.hasReadOnlyRole = roleIds.includes('10');
                    this.user.hasSendDocumentRole = roleIds.includes('11');
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while getting your user.',
                        { error },
                    );
                    this.closeWidget();
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        updateUserImageUrl(imageUrl) {
            this.user.profileImageUrl = imageUrl;
        },

        save() {
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            }

            if (this.user.id) {
                this.update();
            } else {
                this.invite();
            }
        },

        update() {
            var roles = this.generateRoles();

            var hasAtleastOneRole =
                roles.includes('Salesperson') ||
                roles.includes('Marketer') ||
                roles.includes('Admin');
            var isClientUser = this.clientId;

            if (!isClientUser || hasAtleastOneRole) {
                this.loading = true;
                var changes = [
                    {
                        name: 'FirstName',
                        value: this.user.firstName,
                    },
                    {
                        name: 'LastName',
                        value: this.user.lastName,
                    },
                    {
                        name: 'Title',
                        value: this.user.title,
                    },
                    {
                        name: 'Email',
                        value: this.user.email,
                    },
                    {
                        name: 'ProfileImageUrl',
                        value: this.user.profileImageUrl,
                    },
                    {
                        name: 'Signature',
                        value: this.user.signature,
                    },
                    {
                        name: 'Roles',
                        value: roles,
                    },
                ];

                this.$api
                    .patch(`/api/Users/${this.user.id}`, changes)
                    .then(() => {
                        EventBus.$emit('user:update');
                        this.$helpers.showSuccessNotification('User successfully updated.');
                        this.closeWidget();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification('User failed to update.', { error });
                    })
                    .finally(() => {
                        this.loading = false;
                        this.closeWidget();
                    });
            } else {
                this.$helpers.showFailNotification('Please select a role.', {
                    level: LogLevel.INFO,
                });
            }
        },

        generateRoles() {
            var roles = [];

            if (this.user.hasBillingPermission) {
                roles.push('BillingPayment');
            }

            if (this.user.hasSalespersonRole) {
                roles.push('Salesperson');
            }

            if (this.user.hasMDFManagerRole) {
                roles.push('MDFManager');
            }

            if (this.user.hasMarketerRole) {
                roles.push('Marketer');
            }

            if (this.user.hasAdminRole) {
                roles.push('Admin');
            }

            if (this.user.hasReadOnlyRole) {
                roles.push('ReadOnly');
            }

            if (this.user.hasSendDocumentRole) {
                roles.push('SendDocuments');
            }

            if (this.user.hasMDFRequestSettingsManagerPermission) {
                roles.push('MdfRequestSettingsManager');
            }

            if (this.user.hasFundsManagerPermission) {
                roles.push('FundsManager');
            }

            if (this.user.hasFundsPayerPermission) {
                roles.push('FundsPayer');
            }

            if (this.user.hasPOPSubmissionsManagerPermission) {
                roles.push('POPSubmissionsManager');
            }

            if (this.user.hasAccountsManager) {
                roles.push('AccountsManager');
            }

            if (this.user.hasAgencyAdminPermission) {
                roles.push('AgencyAdmin');
            }

            if (this.user.hasMarketingCoordinatorPermission) {
                roles.push('MarketingCoordinator');
            }

            return roles;
        },

        invite() {
            var roles = this.generateRoles();

            var hasAtleastOneRole =
                roles.includes('Salesperson') ||
                roles.includes('Marketer') ||
                roles.includes('Admin');
            var isClientUser = this.clientId;

            if (!isClientUser || hasAtleastOneRole) {
                this.loading = true;
                var request = {
                    clientId: this.clientId,
                    agencyId: this.agencyId,
                    masterAgencyId: this.masterAgencyId,
                    userInvites: [
                        {
                            firstName: this.user.firstName,
                            lastName: this.user.lastName,
                            title: this.user.title,
                            email: this.user.email,
                            roles: roles,
                        },
                    ],
                };

                this.$api
                    .post('/api/Users/Invite', request)
                    .then(() => {
                        this.$helpers.showSuccessNotification('Invite successfully sent.');
                        EventBus.$emit('user:update');
                        this.closeWidget();
                    })
                    .catch((response) => {
                        var message = null;

                        if (response.response.data.ExceptionMessage) {
                            message = response.response.data.ExceptionMessage;
                        } else {
                            message = response.response.data.Message;
                        }

                        this.$helpers.showFailNotification(message, { error: response });
                    })
                    .finally(() => {
                        this.loading = false;
                        this.closeWidget();
                    });
            } else {
                this.$helpers.showFailNotification('Please select a role.', {
                    level: LogLevel.INFO,
                });
            }
        },

        rolesValidation() {
            let isValid = true;
            if (this.isAgencyPage) {
                const {
                    hasAgencyAdminPermission,
                    hasAccountsManager,
                    hasMarketingCoordinatorPermission,
                } = this.user;
                isValid =
                    hasAgencyAdminPermission ||
                    hasAccountsManager ||
                    hasMarketingCoordinatorPermission;
                this.hasRoleErrors = !isValid;
            }

            if (this.clientId) {
                const { hasAdminRole, hasMarketerRole, hasSalespersonRole, hasMDFManagerRole } =
                    this.user;
                isValid =
                    hasAdminRole || hasMarketerRole || hasSalespersonRole || hasMDFManagerRole;
                this.hasRoleErrors = !isValid;
            }

            return isValid;
        },

        getIsValidField(field) {
            const isValid = !(this.$v.user[field].$invalid && this.$v.user[field].$dirty);
            return isValid;
        },

        formatUSD(amount) {
            const formatter = new Intl.NumberFormat('en-US', {
                maximumFractionDigits: 0,
                minimumFractionDigits: 0,
                style: 'currency',
                currency: 'USD',
            });

            return formatter.format(amount);
        },
    },
};
</script>

<style>
.user-widget.widget .content-wrapper {
    padding: 24px;
}

.user-widget .intro-container {
    margin-bottom: 1.5em;
}

.user-widget .current-user-error {
    color: #f30070;
    margin-bottom: 10px;
    margin-left: 36px;
}

.user-widget .section {
    margin-bottom: 1em;
}

.user-widget .section-label {
    position: relative;
    bottom: -12px;
    width: fit-content;
    background: #ffffff;
    margin: 0 auto;
    font-weight: 500;
    padding: 0 47px;
}

.user-widget .input-container .app-input {
    margin-bottom: 1em;
}

.user-widget .section-container {
    border: 2px solid #e9e9ef;
    border-radius: 22px;
    padding: 22px 32px;
}

.user-widget .section-container.has-role-errors {
    border: 2px solid #f30070;
}

.user-widget .error-role-message {
    display: block;
    font-size: 12px;
    margin: 0.8em 0 1.25em 0;
    color: #f30070;
}

.user-widget .required {
    color: var(--color-text-warning);
}

.user-widget .asterisk {
    color: var(--color-text-warning);
    margin-left: 0.25em;
}

.user-widget .checkbox-section {
    display: flex;
}

.user-widget .checkbox-section .app-checkbox {
    margin-right: 1em;
}

.user-widget .checkbox-section span {
    display: block;
    font: 400 14px/1.25em 'Roboto';
}

.user-widget .checkbox-section ul {
    margin-top: 2px;
    padding-left: 16px;
}

.user-widget .transparent-btn {
    background: transparent;
    color: #877d88;
}

.user-widget .transparent-btn:hover {
    background: #e1e0e5;
}

.user-widget .loading-screen {
    width: 100%;
    text-align: center;
}

.user-widget .max-users-wrapper {
    text-align: center;
}

.user-widget .max-users-wrapper .app-button {
    margin: 10px auto 0;
}

.user-widget .billing-notice {
    display: flex;
    gap: 16px;
    padding: 16px;
    background: var(--color-fill-accent-phantom);
    border-radius: 8px;
}

.user-widget .billing-notice-icon {
    flex-shrink: 0;
}

.user-widget .billing-notice-title {
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
}

.user-widget .billing-notice-message {
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
}
</style>
